'use client'
import { PortableText, PortableTextComponents } from '@portabletext/react'
import { urlForImage } from '@/sanity/lib/utils'
import ImageBox from '@/components/ImageBox'
import Video from './Video'

interface ImageValue {
  _type: 'image'
  asset: {
    _ref: string
    _type: 'reference'
  }
  hotspot?: any
  crop?: any
  caption?: string
  alt?: string
  align?: 'left' | 'center' | 'right'
  [key: string]: any
}

interface VideoValue {
  _type: 'video'
  url: string
  id?: string
  service?: string
  thumbnail?: string
  title?: string
  width?: number
  height?: number
}

interface TextItemValue {
  _type: 'sectionText'
  content: any[]
}

type SectionItemValue = ImageValue | VideoValue | TextItemValue

interface SectionValue {
  _type: 'section'
  type: '2' | '3' | 'free'
  items?: SectionItemValue[]
}

const components: PortableTextComponents = {
  types: {
    section: ({ value }: { value: SectionValue }) => {
      if (!value) return null
      const layoutType = value.type
      const items = Array.isArray(value.items) ? value.items : []

      const isFree = layoutType === 'free'
      const containerClass = isFree
        ? 'flex flex-wrap gap-gutter'
        : `grid gap-gutter ${layoutType === '3' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`

      const renderItem = (item: SectionItemValue, index: number) => {
        const key = (item as any)?._key || index

        if ((item as ImageValue)?._type === 'image') {
          const img = item as ImageValue
          if (!img?.asset?._ref) return null
          const alignValue = img.align || 'left'
          const alignClass =
            alignValue === 'left'
              ? 'text-left'
              : alignValue === 'right'
                ? 'text-right'
                : alignValue === 'center'
                  ? 'text-center'
                  : 'text-left'
          return (
            <figure key={key} className={`${isFree ? 'grow basis-[280px]' : ''} ${alignClass}`}>
              <ImageBox
                image={img}
                alt={img.alt || img.caption || ''}
                classesWrapper={`h-auto max-h-[var(--vh-full)] ${alignValue === 'right' ? 'ml-auto' : alignValue === 'center' ? 'mx-auto' : ''}`}
                useOriginalRatio={true}
                placeholder="pixelated"
              />
              {img.caption && (
                <figcaption className={`caption text-gray-70 mt-2 pb-2 ${alignClass}`}>
                  {img.caption}
                </figcaption>
              )}
            </figure>
          )
        }

        if ((item as VideoValue)?._type === 'video') {
          const v = item as VideoValue
          if (!v?.url && !v?.id) return null
          return (
            <figure key={key} className={isFree ? 'grow basis-[320px]' : ''}>
              <div className="w-full">
                <Video
                  url={v.url}
                  id={v.id}
                  service={v.service}
                  width={v.width}
                  height={v.height}
                  autoplay={false}
                />
              </div>
              {v.title && (
                <figcaption className="mt-2 text-center text-sm text-gray-600">
                  {v.title}
                </figcaption>
              )}
            </figure>
          )
        }

        if ((item as TextItemValue)?._type === 'sectionText') {
          const t = item as TextItemValue
          if (!Array.isArray(t.content) || t.content.length === 0) return null
          return (
            <div key={key} className={isFree ? 'grow basis-[280px]' : ''}>
              <PortableText value={t.content} components={components} />
            </div>
          )
        }

        return null
      }

      return (
        <div className={containerClass} data-layer="section-content">
          {items.map(renderItem)}
        </div>
      )
    },
    image: ({ value }: { value: ImageValue }) => {
      if (!value?.asset?._ref) {
        return null
      }
      const alignValue = value.align || 'left'
      const alignClass =
        alignValue === 'left'
          ? 'text-left'
          : alignValue === 'right'
            ? 'text-right'
            : alignValue === 'center'
              ? 'text-center'
              : 'text-left'
      return (
        <figure className={`my-8 max-h-[var(--vh-full)] ${alignClass}`}>
          <ImageBox
            image={value}
            alt={value.alt || value.caption || ''}
            classesWrapper={`h-auto max-h-[var(--vh-full)] ${alignValue === 'right' ? 'ml-auto' : alignValue === 'center' ? 'mx-auto' : ''}`}
            useOriginalRatio={true}
            placeholder="pixelated"
          />
          {value.caption && (
            <figcaption
              className={`mt-2 text-sm text-gray-600 ${
                value.align === 'center'
                  ? 'text-center'
                  : value.align === 'right'
                    ? 'text-right'
                    : 'text-left'
              }`}
            >
              {value.caption}
            </figcaption>
          )}
        </figure>
      )
    },

    video: ({ value }: { value: VideoValue }) => {
      if (!value?.url && !value?.id) {
        return null
      }

      return (
        <figure className="my-8">
          <div className="w-full">
            <Video
              url={value.url}
              id={value.id}
              service={value.service}
              width={value.width}
              height={value.height}
              autoplay={false}
            />
          </div>
          {value.title && (
            <figcaption className="mt-2 text-center text-sm text-gray-600">
              {value.title}
            </figcaption>
          )}
        </figure>
      )
    },
  },

  block: {
    // 일반 문단
    normal: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,

    // 헤딩 스타일들
    h3: ({ children }) => <h3 className="mt-8 mb-4 text-2xl font-bold">{children}</h3>,
    h5: ({ children }) => <h5 className="mt-6 mb-3 text-lg font-semibold">{children}</h5>,
    h6: ({ children }) => <h6 className="mt-4 mb-2 text-base font-bold">{children}</h6>,

    // 인용구
    blockquote: ({ children }) => (
      <blockquote className="my-6 border-l-4 border-gray-300 pl-4 text-gray-700 italic">
        {children}
      </blockquote>
    ),
  },

  marks: {
    // 정렬 마크
    alignLeft: ({ children }) => <span className="block text-left">{children}</span>,
    alignCenter: ({ children }) => <span className="block text-center">{children}</span>,
    alignRight: ({ children }) => <span className="block text-right">{children}</span>,
    alignJustify: ({ children }) => (
      <span className="block text-justify break-all [text-justify:inter-word]">{children}</span>
    ),
    // 링크 처리
    link: ({ children, value }) => {
      const href = value?.href
      if (!href) return <>{children}</>

      const isExternal =
        href.startsWith('http') && !href.includes(process.env.NEXT_PUBLIC_SITE_URL || 'msff.kr')

      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-green-50 underline underline-offset-2 hover:text-green-800"
        >
          {children}
        </a>
      )
    },

    // 기본 텍스트 스타일
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm">{children}</code>
    ),
  },

  list: {
    bullet: ({ children }) => <ul className="mb-4 list-disc space-y-1 pl-6">{children}</ul>,
    number: ({ children }) => <ol className="mb-4 list-decimal space-y-1 pl-6">{children}</ol>,
  },

  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
}

interface RichTextProps {
  value: any
  className?: string
}

export default function RichText({ value, className = '' }: RichTextProps) {
  if (!value) {
    return null
  }

  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      <PortableText value={value} components={components} />
    </div>
  )
}
